import { API_BASE_URL } from '../config/apiBaseUrl';

type LogLevel = 'info' | 'warn' | 'error';

type LoggingConfig = {
  endpoint: string;
  enabled: boolean;
  sampleRate: number;
  mirrorToConsole: boolean;
  appVersion?: string;
  appName?: string;
};

type LoggingIdentity = {
  userId: string | null;
  anonymousId: string | null;
};

type LogPayload = {
  level: LogLevel;
  message: string;
  timestamp: string;
  requestId?: string;
  context?: Record<string, unknown>;
};

const RUNTIME_MODE = import.meta.env.MODE ?? 'development';
const IS_TEST_MODE = RUNTIME_MODE === 'test';
const DEFAULT_ENDPOINT = import.meta.env.VITE_LOGGING_URL || `${API_BASE_URL}/logs`;
const DEFAULT_ENABLED = !IS_TEST_MODE
  && (
    import.meta.env.VITE_LOGGING_ENABLED === 'true'
    || (RUNTIME_MODE !== 'development' && import.meta.env.VITE_LOGGING_ENABLED !== 'false')
  );
const DEFAULT_SAMPLE_RATE = Number.parseFloat(import.meta.env.VITE_LOG_SAMPLE_RATE || '1');
const DEFAULT_MIRROR_TO_CONSOLE = !IS_TEST_MODE && import.meta.env.VITE_LOG_MIRROR_TO_CONSOLE !== 'false';
const MAX_BATCH_SIZE = 10;
const MAX_BATCH_AGE_MS = 5_000;

let config: LoggingConfig = {
  endpoint: DEFAULT_ENDPOINT,
  enabled: DEFAULT_ENABLED,
  sampleRate: Number.isFinite(DEFAULT_SAMPLE_RATE) ? DEFAULT_SAMPLE_RATE : 1,
  mirrorToConsole: DEFAULT_MIRROR_TO_CONSOLE,
};

let identity: LoggingIdentity = {
  userId: null,
  anonymousId: null,
};

let lastRequestId: string | undefined;
let queue: LogPayload[] = [];
let flushTimer: number | null = null;

const clampSampleRate = (value: number) => Math.max(0, Math.min(1, value));

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const extractErrorMessage = (value: unknown): string | undefined => {
  if (value instanceof Error) {
    return value.message;
  }
  if (isObjectRecord(value) && typeof value.message === 'string') {
    return value.message;
  }
  if (typeof value === 'string') {
    return value;
  }
  return undefined;
};

const extractErrorStack = (value: unknown): string | undefined => {
  if (value instanceof Error) {
    return value.stack;
  }
  if (isObjectRecord(value) && typeof value.stack === 'string') {
    return value.stack;
  }
  return undefined;
};

const withErrorDetails = (context: Record<string, unknown>): Record<string, unknown> => {
  const existingMessage =
    typeof context.errorMessage === 'string' && context.errorMessage.trim().length > 0
      ? context.errorMessage
      : undefined;
  const existingStack =
    typeof context.errorStack === 'string' && context.errorStack.trim().length > 0
      ? context.errorStack
      : undefined;

  if (existingMessage && existingStack) {
    return context;
  }

  const candidates = [context.error, context.err, context.reason];
  const derivedMessage = existingMessage ?? candidates.map(extractErrorMessage).find(Boolean);
  const derivedStack = existingStack ?? candidates.map(extractErrorStack).find(Boolean);

  if (!derivedMessage && !derivedStack) {
    return context;
  }

  return {
    ...context,
    ...(derivedMessage ? { errorMessage: derivedMessage } : {}),
    ...(derivedStack ? { errorStack: derivedStack } : {}),
  };
};

const shouldSample = (level: LogLevel) => {
  if (level === 'error') return true;
  return Math.random() < clampSampleRate(config.sampleRate);
};

const sendPayloads = (payloads: LogPayload[]) => {
  if (!config.enabled || typeof window === 'undefined') return;
  if (!payloads.length) return;
  const body = JSON.stringify({ logs: payloads });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(config.endpoint, blob);
    return;
  }

  void fetch(config.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'include',
  }).catch(() => {
    // Logging failures should never surface to users.
  });
};

const flushQueue = () => {
  if (!queue.length) return;
  const payloads = queue;
  queue = [];
  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  sendPayloads(payloads);
};

const scheduleFlush = () => {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = window.setTimeout(() => {
    flushQueue();
  }, MAX_BATCH_AGE_MS);
};

const buildPayload = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>
): LogPayload => {
  const url = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  const baseContext = {
    url,
    userAgent,
    userId: identity.userId,
    anonymousId: identity.anonymousId,
    appVersion: config.appVersion,
    appName: config.appName,
    ...context,
  };

  const normalizedContext = level === 'error' ? withErrorDetails(baseContext) : baseContext;

  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    requestId: lastRequestId,
    context: normalizedContext,
  };
};

const enqueue = (payload: LogPayload) => {
  if (config.mirrorToConsole) {
    const { level, message, context } = payload;
    const consoleMethod =
      level === 'warn'
        ? console.warn
        : level === 'error'
          ? console.error
          : console.info;
    consoleMethod(message, context);
  }
  queue.push(payload);
  if (queue.length >= MAX_BATCH_SIZE) {
    flushQueue();
    return;
  }
  scheduleFlush();
};

const handleWindowError = (event: ErrorEvent) => {
  const error = event.error as Error | undefined;
  logging.log('error', 'window.error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: error?.stack,
  });
};

const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  const reason = event.reason instanceof Error ? event.reason : undefined;
  logging.log('error', 'window.unhandledrejection', {
    reason: reason?.message ?? String(event.reason),
    stack: reason?.stack,
  });
};

export const logging = {
  init: (overrides: Partial<LoggingConfig>) => {
    const next = { ...config, ...overrides };
    config = { ...next, sampleRate: clampSampleRate(next.sampleRate) };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', flushQueue);
      window.addEventListener('pagehide', flushQueue);
      window.addEventListener('error', handleWindowError);
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
    }
  },
  setIdentity: (next: LoggingIdentity) => {
    identity = { ...identity, ...next };
  },
  setRequestId: (requestId?: string) => {
    if (requestId) {
      lastRequestId = requestId;
    }
  },
  log: (level: LogLevel, message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    if (!config.enabled && !config.mirrorToConsole) return;
    if (!shouldSample(level)) return;
    if (!message.trim()) return;
    const payload = buildPayload(level, message, context);
    if (config.mirrorToConsole) {
      const consoleMethod =
        level === 'warn'
          ? console.warn
          : level === 'error'
            ? console.error
            : console.info;
      consoleMethod(payload.message, payload.context);
    }
    if (config.enabled) {
      enqueue(payload);
    }
  },
  info: (message: string, context?: Record<string, unknown>) => {
    logging.log('info', message, context);
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    logging.log('warn', message, context);
  },
  error: (message: string, context?: Record<string, unknown>) => {
    logging.log('error', message, context);
  },
};
